import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Star,
  MapPin,
  Phone,
  Mail,
  BookOpen,
  Users,
  Award,
  Globe,
  Heart,
  Sparkles,
  Trophy,
  Target,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { partnersData } from "../data/partners";

const Home = () => {
  const partners = partnersData;

  const slideShowImages = [
    { src: "/slideshow-optimized/slide-01.jpg" },
    { src: "/slideshow-optimized/slide-02.jpg" },
    { src: "/slideshow-optimized/slide-03.jpg" },
    { src: "/slideshow-optimized/slide-04.jpg" },
    { src: "/slideshow-optimized/slide-05.jpg" },
    { src: "/slideshow-optimized/slide-06.jpg" },
    { src: "/slideshow-optimized/slide-07.jpg" },
    { src: "/slideshow-optimized/slide-08.jpg" },
    { src: "/slideshow-optimized/slide-09.jpg" },
    { src: "/slideshow-optimized/slide-10.jpg" },
    { src: "/slideshow-optimized/slide-11.jpg" },
    { src: "/slideshow-optimized/slide-12.jpg" },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideShowImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slideShowImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slideShowImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? slideShowImages.length - 1 : prev - 1
    );
  };

  const reviews = [
    {
      id: 1,
      name: "Dr. Sarah Johnson",
      title: "Professor of Computer Science, MIT",
      rating: 5,
      text: "We are deeply grateful for ESN Publications' exceptional support in advancing our research. Their dedication to quality and innovation has truly transformed how we share knowledge with the global academic community.",
      date: "2 weeks ago",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
    {
      id: 2,
      name: "Prof. Michael Chen",
      title: "Dean of Engineering, Stanford",
      rating: 5,
      text: "It's been an absolute pleasure working with ESN Publications. Their professional approach and seamless publication process have made our research journey remarkably smooth and rewarding.",
      date: "1 month ago",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
    {
      id: 3,
      name: "Dr. Emily Rodriguez",
      title: "Research Director, Google",
      rating: 5,
      text: "We sincerely appreciate ESN's outstanding conferences and publications. They have graciously helped us connect with brilliant minds worldwide and expand our research impact beyond expectations.",
      date: "2 months ago",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
  ];

  const stats = [
    {
      align: "center",
      icon: BookOpen,
      value: "500+",
      label: "📚 Quality Publications",
      description:
        "Research papers, journals, and reference books published with clarity and quality.",
      accent: "from-amber-400 to-orange-500",
    },
    {
      icon: Users,
      value: "1000+",
      label: "👥 Trusted Authors",
      description:
        "Highly qualified authors and educators trust us for seamless publication support.",
      accent: "from-indigo-500 to-violet-500",
    },
    {
      icon: Globe,
      value: "50+",
      label: "🌍 Global Reach",
      description:
        "Academic collaborations and publications spanning universities worldwide.",
      accent: "from-emerald-500 to-teal-500",
    },
    {
      icon: Award,
      value: "25+",
      label: "🏆 Excellence Awards",
      description:
        "Recognitions earned for delivering professional and impactful scholarly services.",
      accent: "from-orange-500 to-rose-500",
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section
        id="home"
        className="hero-section text-center opacity-50 relative overflow-hidden h-[600px] flex items-center justify-center py-0"
        style={{ background: "none", backgroundImage: "none", backgroundColor: "transparent" }}
      >
        {/* Remove legacy hero pseudo-element and Slideshow Background */}
        <style>{`.hero-section::before{display:none !important}`}</style>
        <div className="hero-slider absolute inset-0 z-0">
          {slideShowImages.map((slide, idx) => (
            <div
              key={slide.src}
              className={`slide ${idx === currentSlide ? "active" : ""}`}
              style={{ backgroundImage: `url(${slide.src})` }}
              aria-hidden={idx === currentSlide ? "false" : "true"}
            />
          ))}
        </div>
        <div className="hero-overlay absolute inset-0 z-20" />
        <div className="hero-grid absolute inset-0 z-[10]" />

        
        {/* Content */}
        <div className="hero-content-inner container-custom relative z-40">
          <div className="animate-fade-in-up">
            <div className="flex items-center justify-center gap-3 mb-4">
              <h1 className="heading-1 text-white drop-shadow-lg" style={{ color: "#ffffff" }}>
                Welcome to ESN Publications
              </h1>
            </div>
            <p
              className="body-large text-orange-100 mb-6 opacity-95 max-w-4xl mx-auto leading-relaxed drop-shadow-md"
              style={{ color: "#f6862a" }}
            >
              We are honored to serve the global academic community by
              advancing excellence through high-quality publications,
              world-class conferences, and meaningful research collaboration.
              Thank you for trusting us with your scholarly journey!
            </p>
            <div className="flex items-center justify-center gap-2 mb-8 text-white opacity-90 drop-shadow-md">
              <span className="text-lg font-medium">
                Empowering Researchers Worldwide Since 2020
              </span>
            </div>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link
                to="/conference/upcoming"
                className="btn btn-secondary btn-lg bg-orange-500 border-orange-500 text-white transform hover:bg-orange-600 hover:scale-105 transition-all duration-300 shadow-xl"
              >
                🎯 Scopus Conferences
              </Link>
              <Link
                to="/awards/upcoming"
                className="btn btn-secondary btn-lg bg-orange-500 border-orange-500 text-white transform hover:bg-orange-600 hover:scale-105 transition-all duration-300 shadow-xl"
              >
                🏆 Scopus Indexed Journal
              </Link>
              <Link
                to="/books/submit-chapter"
                className="btn btn-secondary btn-lg bg-orange-500 border-orange-500 text-white transform hover:bg-orange-600 hover:scale-105 transition-all duration-300 shadow-xl"
              >
                📝 Online Conferences <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>

        <div className="slide-labels z-40">
          {slideShowImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`slide-dot ${idx === currentSlide ? "active" : ""}`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

        <div className="slide-caption z-40" id="slideCaption">
          {slideShowImages[currentSlide].caption}
        </div>
      </section>

      <section className="relative overflow-hidden py-20 bg-gradient-to-br from-[#f5f8ff] via-[#eef4fb] to-[#ffffff]">
        <div className="absolute left-0 top-0 h-56 w-56 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-sky-200/30 blur-3xl" />

        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-10 rounded-full bg-white px-10 py-2 text-sm font-semibold uppercase tracking-[0.25em] text-orange-600 shadow-sm ring-1 ring-orange-100"></span>
            <h2 className="mt-8 text-4xl sm:text-5xl font-semibold text-slate-900">
              🎉 Our Achievements Speak for Themselves
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-8 text-slate-600 max-w-2xl mx-auto">
              We're proud to share these milestones with our wonderful
              community.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="relative overflow-hidden rounded-[40px] bg-slate-50/80 p-6 shadow-[0_35px_90px_rgba(15,23,42,0.08)]">
            <div className="pointer-events-none absolute -top-16 left-1/3 h-52 w-52 rounded-full bg-orange-100/60 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 right-1/4 h-56 w-56 rounded-full bg-sky-100/50 blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-7">
              {stats.map((stat, index) => {
                const Icon = stat.icon;

                return (
                  <div
                    key={index}
                    className="group relative overflow-hidden rounded-[32px] border border-white/60 bg-white/95 px-8 py-12 shadow-[0_25px_70px_rgba(15,23,42,0.08)] transition-transform duration-500 hover:-translate-y-2 hover:shadow-[0_40px_100px_rgba(15,23,42,0.12)]"
                  >
                    <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-slate-100/90 blur-3xl" />
                    <div className="absolute -left-10 bottom-10 h-44 w-44 rounded-full bg-orange-100/60 blur-3xl" />

                    <div className="relative z-10 flex items-center justify-center gap-5">
                      <div
                        className={`relative z-10 flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br ${stat.accent} text-white shadow-lg shadow-slate-900/10`}
                      >
                        <Icon size={26} />
                      </div>

                      <div className="relative z-10">
                        <div
                          className={`text-5xl font-black bg-gradient-to-r ${stat.accent} bg-clip-text text-transparent leading-none`}
                        >
                          {stat.value}
                        </div>
                      </div>
                    </div>

                    <div className="relative z-10 mt-8 text-center">
                      <h3 className="text-xl font-semibold text-slate-900">
                        {stat.label}
                      </h3>
                      <p className="mt-4 text-sm leading-7 text-slate-500">
                        {stat.description}
                      </p>
                    </div>

                    <div
                      className={`relative z-10 mt-8 mx-auto h-1.5 w-16 rounded-full bg-gradient-to-r ${stat.accent} transition-all duration-500 group-hover:w-24`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section
        className="section"
        style={{
          background: "linear-gradient(135deg, #f3e8ff 0%, #f9fafb 100%)",
        }}
      >
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Target size={32} className="text-blue-600" />
              <h2 className="heading-2">About ESN Publications</h2>
              <Target size={32} className="text-blue-600" />
            </div>
            <div
              className="mx-auto mt-8 max-w-3xl rounded-3xl border border-slate-200 bg-slate-50 p-8 shadow-lg"
              style={{ color: "black" }}
            >
              <div className="mx-auto mt-10 max-w-6xl rounded-[32px] border border-slate-200 bg-white/90 backdrop-blur shadow-[0_25px_80px_rgba(15,23,42,0.08)] overflow-hidden">
                <div className="mx-auto mt-10 max-w-6xl">
                  <div className="rounded-[40px] bg-white border border-slate-200 shadow-[0_30px_90px_rgba(15,23,42,0.06)] overflow-hidden">
                    {/* Top Content */}
                    <div className="px-10 sm:px-14 py-12">
                      <div className="max-w-5xl mx-auto">
                        <p className="text-[18px] leading-[2.1] text-slate-700">
                          <span className="font-semibold text-slate-900">
                            {" "}
                            ESN PUBLICATIONS
                          </span>
                          <span className="text-orange-600">
                            (An ISO 9001:2015 Registered Company)
                          </span>
                          is an International institute for Research and
                          Development and a government registered organization
                          for researchers and engineers. The organization
                          started as
                          <span className="font-semibold text-slate-900">
                            Engineering Students Need (ESN)
                          </span>
                          in
                          <span className="font-semibold text-orange-600">
                            2012
                          </span>
                          where many engineering students benefited through
                          publication support. Later in
                          <span className="font-semibold text-orange-600">
                            2015
                          </span>
                          the organization evolved into
                          <span className="font-semibold text-slate-900">
                            ESN Publications (Emerging Science Network)
                          </span>
                          with focus across Education, Science, Engineering,
                          Medical, Management and Technology.
                        </p>

                        <p className="mt-8 text-[18px] leading-[2.1] text-slate-700">
                          ESN Publications works as a common international
                          platform connecting undergraduate students,
                          postgraduates, researchers, professors and educators
                          to share knowledge and publish research. The
                          organization also conducts International Conferences,
                          Management Development Programs and academic events
                          that enhance knowledge, skills and professional
                          development.
                        </p>
                        <br></br>
                      </div>
                    </div>

                    {/* Highlight Strip */}
                    <div className="border-y bg-gradient-to-r from-orange-50 via-white to-purple-50 px-10 py-10">
                      <div className="flex flex-wrap justify-center gap-4">
                        <div className="rounded-full bg-white px-6 py-3 shadow-sm border">
                          🏆 3× World Book of Records
                        </div>
                        <div className="rounded-full bg-white px-6 py-3 shadow-sm border">
                          📖 Thickest Book in the World
                        </div>
                        <div className="rounded-full bg-white px-6 py-3 shadow-sm border">
                          🌍 Longest International Conference
                        </div>
                        <div className="rounded-full bg-white px-6 py-3 shadow-sm border">
                          🇮🇳 2× India Book of Records
                        </div>
                        <div className="rounded-full bg-white px-6 py-3 shadow-sm border">
                          ✍️ 1111 Poems • 1111 Poets
                        </div>
                      </div>
                    </div>

                    {/* Bottom Achievement */}

                    <div className="relative overflow-hidden">
                      {/* Background */}

                      <div className="absolute inset-0 bg-gradient-to-br from-[#fff7ed] via-white to-[#f5f3ff]" />
                      
                    
                  </div>
                </div>
              </div>

              {/* Bottom Highlight */}
              <br></br>
              <div className="border-t bg-gradient-to-r from-orange-50 to-purple-50 px-10 py-8">
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="rounded-full bg-white px-5 py-3 shadow-sm">
                    🏛️ International Research Institute
                  </div>

                  <div className="rounded-full bg-white px-5 py-3 shadow-sm">
                    📚 Research Publications
                  </div>

                  <div className="rounded-full bg-white px-5 py-3 shadow-sm">
                    🌍 Global Academic Platform
                  </div>

                  <div className="rounded-full bg-white px-5 py-3 shadow-sm">
                    🏆 Guinness World Record Holder
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="grid gap-8">
            <div className="card transform hover:scale-105 transition-all duration-300 border-l-4 border-blue-500">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-2xl shadow-lg">
                  🎯
                </div>
                <div>
                  <h3 className="heading-4 mb-3 text-blue-800">
                    🌟 Our Mission
                  </h3>
                  <p className="body-medium text-gray-700 leading-relaxed">
                    We are honored to provide a premier platform where
                    researchers and academics can publish exceptional work,
                    share valuable knowledge, and advance their fields through
                    meaningful collaboration and innovation. Your success is our
                    greatest achievement.
                  </p>
                </div>
              </div>
            </div>

            <div className="card transform hover:scale-105 transition-all duration-300 border-l-4 border-purple-500">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white text-2xl shadow-lg">
                  🔮
                </div>
                <div>
                  <h3 className="heading-4 mb-3 text-purple-800">
                    ✨ Our Vision
                  </h3>
                  <p className="body-medium text-gray-700 leading-relaxed">
                    We aspire to be the most trusted global platform for
                    academic publishing and research collaboration, graciously
                    fostering innovation and excellence in scholarly
                    communication across all disciplines. Together, we're
                    building the future of academic excellence.
                  </p>
                </div>
              </div>
            </div>

            <div className="card transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl flex items-center justify-center text-white text-2xl shadow-lg">
                  💝
                </div>
                <div>
                  <h3 className="heading-4 mb-3 text-green-800">
                    🤝 Our Values
                  </h3>
                  <p className="body-medium text-gray-700 leading-relaxed">
                    We deeply value integrity, excellence, and collaboration.
                    Every researcher deserves respectful support, quality
                    service, and opportunities to shine. We're grateful to be
                    part of your academic journey and committed to your success.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div></div>
      </section>

      {/* Partners Section */}
      <section
        className="section"
        style={{
          background: "linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%)",
        }}
      >
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Trophy size={32} className="text-yellow-600" />
              <h2 className="heading-2">🤝 Our Esteemed Partners</h2>
              <Trophy size={32} className="text-yellow-600" />
            </div>
            <p className="body-large text-gray-700">
              We're privileged to collaborate with prestigious institutions and
              organizations worldwide 🌍
            </p>
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-8 items-center justify-items-center">
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="partner-logo-container hover-lift"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="partner-logo"
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/120x60/e2e8f0/64748b?text=Logo${partner.id}`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section
        className="section"
        style={{
          background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
        }}
      >
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <h2 className="heading-2">
                💬 What Our Wonderful Community Says
              </h2>
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={28}
                  className="text-yellow-400 fill-current animate-pulse"
                />
              ))}
              <span className="ml-3 text-2xl font-bold text-gray-900">
                4.9/5
              </span>
            </div>
            <p className="body-large text-gray-700">
              ✨ Based on 150+ heartfelt Google Reviews from our valued
              community ✨
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div
                key={review.id}
                className="card animate-fade-in-up"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="body-medium mb-6 italic">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={review.avatar}
                    alt={review.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">
                      {review.name}
                    </div>
                    <div className="text-sm text-gray-500">{review.title}</div>
                    <div className="text-xs text-gray-400">{review.date}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="section"
        style={{
          background: "linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)",
        }}
      >
        <div className="container-custom">
          <div className="card-gradient text-center p-12 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sparkles size={32} className="text-yellow-300 animate-bounce" />
              <h2 className="heading-2 text-white">
                🚀 Ready to Begin Your Journey?
              </h2>
              <Sparkles size={32} className="text-yellow-300 animate-bounce" />
            </div>
            <p className="body-large text-white mb-8 opacity-95 max-w-3xl mx-auto leading-relaxed">
              💫 We warmly invite you to join thousands of distinguished
              researchers and academics who have trusted ESN Publications with
              their scholarly work. Let's create something amazing together! 💫
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link
                to="/books/submit-chapter"
                className="btn btn-secondary btn-lg transform hover:scale-110 transition-all duration-300 shadow-xl"
              >
                📚 Submit Your Work
              </Link>
              <Link
                to="/contact"
                className="btn btn-outline btn-lg text-white border-white hover:bg-white hover:text-gray-900 transform hover:scale-110 transition-all duration-300"
              >
                💬 Let's Connect
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        className="section"
        style={{
          background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
        }}
      >
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Mail size={32} className="text-blue-600" />
              <h2 className="heading-2">📞 We'd Love to Hear From You!</h2>
              <Mail size={32} className="text-blue-600" />
            </div>
            <p className="body-large text-gray-700">
              🤝 Ready to collaborate? We're here to support your academic
              journey with a smile!
            </p>
          </div>

          <div className="contact-grid">
            {/* Contact Info */}
            <div className="space-y-6">
              <div className="card text-center transform hover:scale-105 transition-all duration-300 border-t-4 border-blue-500">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Phone size={28} className="text-white" />
                </div>
                <h3 className="heading-4 mb-3 text-blue-800">
                  📞 Call Us Anytime
                </h3>
                <p className="body-medium text-gray-700 font-semibold">
                  +91 88381 73189 <br />
                  +91 86677 67076
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  We're always happy to help!
                </p>
              </div>

              <div className="card text-center transform hover:scale-105 transition-all duration-300 border-t-4 border-purple-500">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Mail size={28} className="text-white" />
                </div>
                <h3 className="heading-4 mb-3 text-purple-800">✉️ Email Us</h3>
                <p className="body-medium text-gray-700 font-semibold">
                  esnpublications@gmail.com
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Quick responses guaranteed!
                </p>
              </div>

              <div className="card text-center transform hover:scale-105 transition-all duration-300 border-t-4 border-green-500">
                <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <MapPin size={28} className="text-white" />
                </div>
                <h3 className="heading-4 mb-3 text-green-800">
                  🏢 Visit Our Office
                </h3>
                <p className="body-medium text-gray-700">
                  3/151 A, Muthuramalingapuram,
                  <br />
                  Kalloorani P.O, Tk, Aruppukkottai,
                  <br />
                  Tamil Nadu 626105
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Coffee and warm welcome awaits!
                </p>
              </div>
            </div>

            {/* Map */}
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3929.8234567890123!2d78.1650608!3d9.4638088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b013bb041fc3af3%3A0xa2c85233d5076c57!2sESN%20Publications!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                width="100%"
                height="100%"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ESN Publications Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
